/**
 * The original Dojo library and immutable review batches, bundled at build time.
 * Review drafts live outside the learner seed directory and are never promoted
 * to the live catalog by this reader. Original item IDs remain unchanged so
 * browser notes continue to refer to the exact edition a reviewer read.
 *
 * Reviewer-only material (the answer key) is deliberately included here: this
 * page exists so the HOW Institute can read every vignette and inspect where
 * Waldo hides. The learner-facing payload (build_unit_client_payload) still
 * strips it.
 */

export type ReviewSpan = {
  id: string;
  kind: 'admirable' | 'adequate';
  quote: string;
  note: string;
  paragraph: number;
  cost_evidence?: string;
  cost_paragraph?: number;
  observation_type?: 'strength' | 'improvement' | 'inquiry';
};

export type ReviewItem = {
  id: string;
  unitNumber: number;
  imperative: string;
  title: string;
  levelKey: 'new_manager' | 'mid_level' | 'senior_executive';
  level: string;
  leader: string;
  words: number;
  enactWords: number;
  intake: string[];
  reactWell: string;
  reactBetter: string;
  exemplar: string[];
  flipAxis: string;
  enact: string[];
  question: string;
  spans: ReviewSpan[];
  editorialContract?: string;
};

export const ORIGINAL_REVIEW_BATCH = 'original';
// Choose the review edition explicitly; a newly added draft must not replace it.
export const DEFAULT_REVIEW_BATCH = 'single-situation-2026-11-03';

export type ReviewBatch = {
  id: string;
  label: string;
  createdAt?: string;
  status: 'original' | 'review_draft' | 'published';
  expectedVariantCount: number;
  variantCount: number;
  description?: string;
};

type ReviewBatchManifest = Omit<ReviewBatch, 'variantCount'>;

type UnitFile = {
  scenario_id: string;
  title: string;
  imperative_label: string;
  imperative_order?: number;
  unit: {
    intake_questions?: string[];
    flip_axis?: string;
    variants: Record<
      string,
      {
        leader_name?: string;
        intake_questions?: string[];
        exemplar_paragraphs: string[];
        react_prompts?: { well?: string; better?: string };
        enact_paragraphs: string[];
        enact_question?: string;
        flip_axis?: string;
        highlight_spans?: ReviewSpan[];
        editorial_contract?: string;
      }
    >;
  };
};

const LEVELS: Array<[ReviewItem['levelKey'], string]> = [
  ['new_manager', 'New manager'],
  ['mid_level', 'Mid-level leader'],
  ['senior_executive', 'Senior / executive'],
];

// Freeze the edition behind existing note IDs when the learner seeds change.
const originalModules = import.meta.glob<{ default: UnitFile }>(
  '../../../../railway-howie-service/resources/dojo-review-original/unit_*.json',
  { eager: true },
);

const batchManifests = import.meta.glob<{ default: ReviewBatchManifest }>(
  '../../../../railway-howie-service/resources/dojo-review-batches/*/manifest.json',
  { eager: true },
);

const batchModules = import.meta.glob<{ default: UnitFile }>(
  '../../../../railway-howie-service/resources/dojo-review-batches/*/unit_*.json',
  { eager: true },
);

function unitsForBatch(batchId: string): UnitFile[] {
  const modules = batchId === ORIGINAL_REVIEW_BATCH ? originalModules : batchModules;
  return Object.keys(modules)
    .filter((path) => batchId === ORIGINAL_REVIEW_BATCH || path.split('/').slice(-2)[0] === batchId)
    .sort()
    .map((path) => modules[path].default);
}

export function loadReviewBatches(): ReviewBatch[] {
  const count = (batchId: string) => unitsForBatch(batchId)
    .reduce((total, unit) => total + LEVELS.filter(([level]) => unit.unit.variants[level]).length, 0);
  const originalCount = count(ORIGINAL_REVIEW_BATCH);
  const batches: ReviewBatch[] = [{
    id: ORIGINAL_REVIEW_BATCH,
    label: 'Original library',
    status: 'original',
    expectedVariantCount: originalCount,
    variantCount: originalCount,
  }];
  for (const path of Object.keys(batchManifests).sort()) {
    const manifest = batchManifests[path].default;
    // The directory is part of the immutable identity, not a display label.
    if (manifest.id !== path.split('/').slice(-2)[0] || manifest.id === ORIGINAL_REVIEW_BATCH) continue;
    batches.push({ ...manifest, variantCount: count(manifest.id) });
  }
  return batches;
}

const wordCount = (paragraphs: string[]) => paragraphs.join(' ').split(/\s+/).filter(Boolean).length;

export function loadReviewLibrary(batchId = DEFAULT_REVIEW_BATCH): ReviewItem[] {
  if (!loadReviewBatches().some((batch) => batch.id === batchId)) return [];
  const units = unitsForBatch(batchId);
  const items: ReviewItem[] = [];
  for (const unit of units) {
    const unitNumber = Number(unit.scenario_id.slice(5, 7)) || unit.imperative_order || 0;
    for (const [levelKey, level] of LEVELS) {
      const v = unit.unit.variants[levelKey];
      if (!v) continue;
      items.push({
        id: `${batchId === ORIGINAL_REVIEW_BATCH ? '' : `${batchId}__`}${unit.scenario_id}__${levelKey}`,
        unitNumber,
        imperative: unit.imperative_label,
        title: unit.title,
        levelKey,
        level,
        leader: v.leader_name || '',
        words: wordCount(v.exemplar_paragraphs),
        enactWords: wordCount([...v.enact_paragraphs, v.enact_question || '']),
        intake: v.intake_questions?.length ? v.intake_questions : unit.unit.intake_questions || [],
        reactWell: v.react_prompts?.well || '',
        reactBetter: v.react_prompts?.better || '',
        exemplar: v.exemplar_paragraphs,
        flipAxis: v.flip_axis || unit.unit.flip_axis || '',
        enact: v.enact_paragraphs,
        question: v.enact_question || '',
        spans: v.highlight_spans || [],
        editorialContract: v.editorial_contract,
      });
    }
  }
  return items;
}

export type PaintedSegment =
  | { text: string }
  | { text: string; mark: 'g' | 'y' | 'cost'; tag: string };

/**
 * Split one exemplar paragraph into plain and marked segments, by exact
 * substring — the same rule the learner-side highlighter uses. Overlaps keep
 * the earliest span.
 */
export function paintParagraph(text: string, spans: ReviewSpan[], paragraphIndex: number): PaintedSegment[] {
  const marks: Array<{ i: number; j: number; mark: 'g' | 'y' | 'cost'; tag: string }> = [];
  for (const s of spans) {
    if (s.paragraph === paragraphIndex && s.quote) {
      const i = text.indexOf(s.quote);
      if (i >= 0) marks.push({ i, j: i + s.quote.length, mark: s.kind === 'admirable' ? 'g' : 'y', tag: s.id });
    }
    if (s.kind === 'adequate' && s.cost_evidence && s.cost_paragraph === paragraphIndex) {
      const i = text.indexOf(s.cost_evidence);
      if (i >= 0) marks.push({ i, j: i + s.cost_evidence.length, mark: 'cost', tag: `${s.id} cost` });
    }
  }
  marks.sort((a, b) => a.i - b.i);
  const out: PaintedSegment[] = [];
  let cursor = 0;
  for (const m of marks) {
    if (m.i < cursor) continue;
    if (m.i > cursor) out.push({ text: text.slice(cursor, m.i) });
    out.push({ text: text.slice(m.i, m.j), mark: m.mark, tag: m.tag });
    cursor = m.j;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor) });
  return out;
}

const READ_KEY = 'howie:dojo-review:read';
const NOTES_KEY = 'howie:dojo-review:notes';

export function loadReadSet(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(READ_KEY) || '[]') as string[]);
  } catch {
    return new Set();
  }
}
export function saveReadSet(read: Set<string>): void {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify([...read]));
  } catch {
    /* storage unavailable — progress is a convenience, not a requirement */
  }
}
export function loadNotes(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(NOTES_KEY) || '{}') as Record<string, string>;
  } catch {
    return {};
  }
}
export function saveNotes(notes: Record<string, string>): void {
  try {
    localStorage.setItem(NOTES_KEY, JSON.stringify(notes));
  } catch {
    /* see above */
  }
}

/** A mailto: carrying every note, so a reviewer can send them in one go. */
export function buildNotesMailto(items: ReviewItem[], notes: Record<string, string>, to: string, batch?: ReviewBatch): string {
  const lines: string[] = ['Notes from the Practice review surface', ''];
  if (batch && batch.id !== ORIGINAL_REVIEW_BATCH) lines.push(`Review batch: ${batch.label} (${batch.id})`, '');
  for (const it of items) {
    const note = (notes[it.id] || '').trim();
    if (!note) continue;
    lines.push(`${it.unitNumber}. ${it.imperative} — ${it.level} (${it.leader})`, note, '');
  }
  const body = lines.join('\n');
  return `mailto:${to}?subject=${encodeURIComponent('Practice review notes')}&body=${encodeURIComponent(body)}`;
}
